import { ENTER_NAME, ESC_NAME, NUMPAD_ENTER_NAME } from '@anm/constants/keyCodes';
import noop from '@anm/helpers/noop';
import { brToNewLine } from '@anm/helpers/string/newLineToBr';
import sanitizeText from '@anm/helpers/string/sanitizeText';
import { useCallback, useEffect, ChangeEvent, FC, KeyboardEvent } from 'react';

import { Field, FieldInput, FieldWrapper, ValueHolder } from './Wrapper';
import { useEditableField } from './hooks';

export { FieldWrapper, ValueHolder };

export type EditableFieldSizes = 'small' | 'medium';
export type EditableFieldVariants = 'crop' | 'multirows' | 'cropMultiline';
export type EditableFieldHoverVariants = 'pencil';

export type EditableFieldProps = {
  onChange: (value: string) => void;
  initialValue: string;
  tag?: keyof JSX.IntrinsicElements;
  size?: EditableFieldSizes;
  variant?: EditableFieldVariants;
  fieldTag?: 'input' | 'textarea';
  isActive?: boolean;
  maxLength?: number;
  placeholder?: string;
  isFocusedOnClick?: boolean;
  hoverVariant?: EditableFieldHoverVariants;
  onFieldFocused?: (isFocused: boolean) => void;
};

const EditableField: FC<EditableFieldProps> = ({
  isActive,
  tag = 'span',
  maxLength = 256,
  variant = 'crop',
  fieldTag = 'textarea',
  isFocusedOnClick = true,
  placeholder = 'Add name',
  size: fieldSize = 'small',
  hoverVariant,
  onFieldFocused = noop,
  ...otherProps
}) => {
  const [
    value,
    setValue,
    isFocused,
    onInputEnd,
    setIsFocused,
    inputElement,
    isEscPressed,
    setIsEscPressed,
    valueContainerRef
  ] = useEditableField(otherProps);

  useEffect(() => {
    onFieldFocused(isFocused);
  }, [isFocused, onFieldFocused]);

  useEffect(() => {
    if (isActive) {
      inputElement.current!.focus();
      inputElement.current!.select();
    }
  }, [isActive]);

  const onTextareaChange = useCallback(({ target: { value } }: ChangeEvent<HTMLTextAreaElement>) => {
    setValue(value);
  }, []);

  const handleClick = useCallback(() => {
    if (!isFocusedOnClick) return;
    setIsFocused(true);
    inputElement.current!.focus();
    inputElement.current!.select();
  }, [isFocusedOnClick, inputElement.current]);

  const placeHolderValue = value.trim() || otherProps.initialValue;

  const onBlur = useCallback(() => {
    !isEscPressed && onInputEnd();
    setValue(placeHolderValue);
  }, [isEscPressed, placeHolderValue]);

  const onKeyDown = useCallback(
    ({ code, shiftKey }: KeyboardEvent<HTMLTextAreaElement>) => {
      const canBlur = [ENTER_NAME, NUMPAD_ENTER_NAME].includes(code) && !shiftKey && inputElement.current;
      canBlur && inputElement.current!.blur();

      if (code === ESC_NAME && isFocusedOnClick) {
        setIsEscPressed(true);
        setValue(value);
        setIsFocused(false);
      } else setIsEscPressed(false);
    },
    [inputElement.current, value, isFocusedOnClick]
  );

  const canShow = isActive || isFocused;
  const ownHeight = valueContainerRef?.current?.clientHeight;

  return (
    <FieldWrapper>
      <Field
        onClick={handleClick}
        ref={valueContainerRef}
        {...{
          variant,
          canShow,
          fieldSize,
          hoverVariant,
          isFocusedOnClick,
          ...(ownHeight && { ownHeight })
        }}
      >
        <ValueHolder as={tag} dangerouslySetInnerHTML={{ __html: sanitizeText(placeHolderValue) }} />
      </Field>
      <FieldInput
        as={fieldTag}
        ref={inputElement}
        isActive={canShow}
        onChange={onTextareaChange}
        onFocus={() => setIsFocused(true)}
        value={brToNewLine(value)}
        {...{
          maxLength,
          fieldSize,
          placeholder,
          onBlur,
          onKeyDown,
          hoverVariant
        }}
      />
    </FieldWrapper>
  );
};

export default EditableField;
